import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, EnergySavingFilters } from '../services/api';
import toast from 'react-hot-toast';

export const useEnergySavings = (params?: EnergySavingFilters) => {
  return useQuery({
    queryKey: ['energySavings', params],
    queryFn: () => apiService.getEnergySavings(params),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useEnergySavingStats = () => {
  return useQuery({
    queryKey: ['energySavingStats'],
    queryFn: () => apiService.getEnergySavingStats(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useEnergyPartners = () => {
  return useQuery({
    queryKey: ['energyPartners'],
    queryFn: () => apiService.getEnergyPartners(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
};

export const useEnergySavingExportHistory = (limit?: number) => {
  return useQuery({
    queryKey: ['energySavingExportHistory', limit],
    queryFn: () => apiService.getEnergySavingExportHistory(limit),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useBulkExportEnergySavings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (energySavingIds: string[]) => apiService.bulkExportEnergySavings(energySavingIds),
    onSuccess: (data) => {
      // Download the Excel file
      if (data.excelBuffer) {
        const byteCharacters = atob(data.excelBuffer);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `energy-savings-export-${data.batchId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Exported ${data.count} energy savings (€${data.totalAmount.toFixed(2)})`);
      queryClient.invalidateQueries({ queryKey: ['energySavings'] });
      queryClient.invalidateQueries({ queryKey: ['energySavingStats'] });
      queryClient.invalidateQueries({ queryKey: ['energySavingExportHistory'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to export energy savings');
    },
  });
};

export const useBulkRejectEnergySavings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ energySavingIds, reason }: { energySavingIds: string[]; reason: string }) =>
      apiService.bulkRejectEnergySavings(energySavingIds, reason),
    onSuccess: (data) => {
      toast.success(`Rejected ${data.count} energy savings`);
      queryClient.invalidateQueries({ queryKey: ['energySavings'] });
      queryClient.invalidateQueries({ queryKey: ['energySavingStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject energy savings');
    },
  });
};

export const useBulkMarkEnergySavingsComplete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (energySavingIds: string[]) => apiService.bulkMarkEnergySavingsComplete(energySavingIds),
    onSuccess: (data) => {
      toast.success(`Marked ${data.count} energy savings as complete`);
      queryClient.invalidateQueries({ queryKey: ['energySavings'] });
      queryClient.invalidateQueries({ queryKey: ['energySavingStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to mark energy savings as complete');
    },
  });
};
